import type { CourseIndex } from '@sw/geo'
import { indexCourse } from '@sw/geo'
import type { Emitter, Gate, Region, Rule, Scope, WorldDefinition } from '@sw/schema'
import { resolveRuleScope } from '@sw/schema'
import type { CronMatcher } from './cron.js'
import { compileCron } from './cron.js'

/**
 * A world prepared for evaluation: lookups built once, courses indexed once,
 * and every cron expression parsed up front so a malformed schedule fails at
 * publish rather than at showtime.
 */
export interface CompiledWorld {
  definition: WorldDefinition
  regions: ReadonlyMap<string, Region>
  gates: ReadonlyMap<string, Gate>
  courses: ReadonlyMap<string, CourseIndex>
  emitters: ReadonlyMap<string, Emitter>
  rulesById: ReadonlyMap<string, Rule>
  scopeOf: ReadonlyMap<string, Scope>
  rulesByScope: Readonly<Record<Scope, readonly Rule[]>>
  /** Keyed `ruleId:triggerIndex`. */
  cron: ReadonlyMap<string, CronMatcher>
}

export function triggerKey(ruleId: string, triggerIndex: number): string {
  return `${ruleId}:${triggerIndex}`
}

export function compileWorld(definition: WorldDefinition): CompiledWorld {
  const courses = new Map<string, CourseIndex>()
  for (const course of definition.courses) courses.set(course.id, indexCourse(course))

  const scopeOf = new Map<string, Scope>()
  const listener: Rule[] = []
  const session: Rule[] = []
  const cron = new Map<string, CronMatcher>()

  for (const rule of definition.rules) {
    const scope = resolveRuleScope(rule)
    scopeOf.set(rule.id, scope)
    ;(scope === 'listener' ? listener : session).push(rule)

    rule.triggers.forEach((trigger, index) => {
      if (trigger.type === 'wallClock') {
        cron.set(triggerKey(rule.id, index), compileCron(trigger.cron))
      }
    })
  }

  return {
    definition,
    regions: new Map(definition.regions.map((r) => [r.id, r])),
    gates: new Map(definition.gates.map((g) => [g.id, g])),
    courses,
    emitters: new Map(definition.emitters.map((e) => [e.id, e])),
    rulesById: new Map(definition.rules.map((r) => [r.id, r])),
    scopeOf,
    rulesByScope: { listener, session },
    cron,
  }
}
